import AppTextField from "@/components/AppTextField";
import FormItem from "@/components/FormItem";
import NationCodeAutoComplete from "@/components/NationCodeAutoComplete";
import SendCodeInput from "@/components/SendCodeInput";
import AppLoadingButton from "@/components/mui/LoadingButton";
import API from "@/constant/API";
import { getTaskId, getUserInfo, setTaskRuleParams } from "@/redux/slices/user";
import { dispatch } from "@/redux/store";
import axiosInstance from "@/utils/axios";
import { REGEXP } from "@/utils/validateUtils";
import { Box, Grid, Typography } from "@mui/material";
import { useFormik } from "formik";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import * as Yup from "yup";
import classes from './index.module.css';
import AppModal from "@/components/AppModal";
import Security2FA from "@/pages/security/Security2FA";
import useToggle from "@/hook/useToggle";
import { VerifyAction } from "@/constant/VerifyConstant";

function GuideBindMobile({onSuccess}) {
  const {t} = useTranslation();
  const [loading, setLoading] = useState(false);

  const [step, setStep] = useState(1);
  const [autoSendFlag, setAutoSendFlag] = useState(null);

  const [country, setCountry] = useState(null);
  const [countryRequiredError, setCountryRequiredError] = useState(false);

  const signRef = useRef(null);

  const {
    toggle: show2FA,
    onToggle: onToggleShow2FA,
  } = useToggle(false);

  const {loading: userLoading} = useSelector((state) => state.user);

  let yupShape = {
    mobile: Yup.string()
      .trim()
      .matches(REGEXP.PHONE, "mobile_format_error")
      .required("this_field_is_required"),
  };
  if (step == 2) {
    yupShape.code = Yup.string()
      .trim()
      .matches(REGEXP.VERIFY_CODE, "verify_code_format_error")
      .required("this_field_is_required");
  }
  const validationSchema = Yup.object().shape(yupShape);

  const _onBind = async () => {
    setLoading(true);
    if (!country) return;
    const params = {
      ...values,
      nationCode: country.areaCode,
      sign: signRef.current,
      taskId: getTaskId(),
    };
    try {
      await axiosInstance.post(API.BIND_MOBILE, params);
      dispatch(await getUserInfo());
      if (onSuccess) onSuccess();
    } finally {
      setLoading(false);
    }
  };

  const {
    errors,
    values,
    touched,
    setFieldValue,
    handleChange,
    handleBlur,
    handleSubmit,
  } = useFormik({
    initialValues: {
      mobile: "",
      code: "",
    },
    validationSchema,
    validateOnBlur: true,
    validateOnChange: false,
    onSubmit: () => {
      if (step == 1) {
        if (!country) {
          console.error(values.mobile, REGEXP.ALPHA_NUMERIC.test(values.mobile))
          setCountryRequiredError(true);
          return;
        }
        _on2FA();
      } else {
        _onBind();
      }
    },
  });

  const _onPrev = () => {
    setStep(1);
    setFieldValue("code", "");
  };
  
  const _on2FA = () => {
    setTaskRuleParams({
      action: VerifyAction.BIND_SMS,
    });
    onToggleShow2FA();
  }

  const _on2FAAllPass = () => {
    onToggleShow2FA();
    _onNext();
  }

  const _onNext = async () => {
    setLoading(true);
    if (!country) return;
    try {
      const res = await axiosInstance.post(API.BIND_MOBILE_EXIST, {
        nationCode: country.areaCode,
        mobile: values.mobile,
      });

      signRef.current = res.sign;
      setStep(2);
      setAutoSendFlag(Symbol());
    } finally {
      setLoading(false);
    }
  };

  const _onChange = (record) => {
    setCountryRequiredError(!record);
    setCountry(record);
    _onMobileInputClick();
  };

  const _onMobileInputClick = () => {
    if (step == 2) {
      _onPrev();
    }
  };

  return (
    <Box className={classes.content}>
      <form className="form" onSubmit={handleSubmit}>
        <Box className="form-title-box">
          <Typography className="form-subtitle">
            {t("enter_the_details_below_to_get_your_card")}
          </Typography>
        </Box>
        <Grid container columnSpacing={1}>
          <Grid item xs={4}>
            <FormItem label={t("country_code")}>
              <NationCodeAutoComplete
                fullWidth
                value={country}
                onChange={_onChange}
              />
              {countryRequiredError && <p className="form-error Mui-error">{t('this_field_is_required')}</p>}
            </FormItem>
          </Grid>

          <Grid item xs={8}>
            <FormItem label={t("phone_number")}>
              <AppTextField
                fullWidth
                autoComplete="off"
                // type="number"
                name="mobile"
                size="small"
                clearable
                variant="outlined"
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, "")
                  e.target.value = val;
                  handleChange(e);
                }}
                onBlur={handleBlur}
                onClear={() => setFieldValue("mobile", "")}
                error={Boolean(touched.mobile && errors.mobile)}
                helperText={touched.mobile && t(errors.mobile)}
                onClick={_onMobileInputClick}
                value={values.mobile}
              />
            </FormItem>
          </Grid>
        </Grid>

        {step == 2 && <FormItem label={t('please_enter_your_verify_code')}>
          <SendCodeInput size="small" isMobile={true} username={values.mobile} nationCode={country?.areaCode}
                         name="code" error={Boolean(touched.code && errors.code)} action={VerifyAction.BIND_SMS}
                         sign={signRef.current}
                         helperText={touched.code && t(errors.code)} autoSend={autoSendFlag}
                         onChange={handleChange} onBlur={handleBlur} value={values.code}/>
        </FormItem>}

        <Box sx={{paddingTop: 1, paddingBottom: 1}}>
          <AppLoadingButton
            type="submit"
            fullWidth
            loading={userLoading["getUserInfo"] || loading}
            size="large"
            variant="contained"
          >
            {step == 1 ? t("next_step") : t("submit")}
          </AppLoadingButton>
          {/* {step == 2 && <Button fullWidth size="large" variant="text" onClick={_onPrev}>{t('prev_step')}</Button>} */}
        </Box>
      </form>

      <AppModal open={show2FA} onClose={onToggleShow2FA}>
        <Security2FA callback={_on2FAAllPass} onClose={onToggleShow2FA}/>
      </AppModal> 
    </Box>
  );
}

export default GuideBindMobile;
